import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/commandes-vn';
import './CommandesVN.css';

// ═══════════════════════════════════════════════════════════════════════════
// Commandes VN — remplace le fichier Excel de suivi des commandes VN.
//   · Couverture : une ligne par version ; ventes nettes et stock viennent du
//     DMS (A97 Data), les commandes de cette page. Le calcul est côté serveur
//     (routes/commandes-vn.js → versionner()) : l'écran n'additionne rien qu'il
//     ne reçoive déjà.
//   · Commandes : dossiers → lots (quantité, ETA, n° constructeur, paiement).
//   · Calendrier : arrivées et décaissements prévus, mois par mois.
//   · Paramètres : délais, versions suivies, correspondance des libellés DMS.
// ═══════════════════════════════════════════════════════════════════════════

const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const moisLib = (ym) => { const [a, m] = ym.split('-'); return `${MOIS_COURTS[Number(m) - 1]} ${a.slice(2)}`; };
const nf = new Intl.NumberFormat('fr-FR');
const eur = (v) => (v == null ? '—' : `${nf.format(Math.round(Number(v)))} €`);
const keur = (v) => (v == null ? '—' : Math.abs(v) >= 1e6 ? `${(v / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} M€` : `${nf.format(Math.round(v / 1000))} k€`);
const dateFr = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR') : '—');
const isoJour = (v) => (v ? String(v).slice(0, 10) : '');
const aujourdhui = () => new Date().toISOString().slice(0, 10);

const ETATS = {
  rupture: { label: 'Rupture', classe: 'badge-pill badge-error', aide: 'Le stock, plus ce qui arrive avant le délai de commande, ne couvre pas les ventes prévues sur ce délai.' },
  a_commander: { label: 'À commander', classe: 'badge-pill badge-warning', aide: 'Les ressources (stock + commandes) ne couvrent pas le besoin sur le délai de commande + la couverture voulue.' },
  ok: { label: 'Couvert', classe: 'badge-pill badge-success', aide: 'Les ressources couvrent le besoin.' },
  a_saisir: { label: 'Prévision à saisir', classe: 'badge-pill badge-info', aide: 'Des mois de la période du besoin n’ont pas de prévision : le chef des ventes doit les saisir (onglet Prévisions) avant tout calcul de commande.' },
  surstock: { label: 'Surstock', classe: 'badge-pill badge-neutral', aide: 'Plus de 3 mois au-delà de la couverture voulue.' },
  inactif: { label: '—', classe: 'text-muted', aide: 'Ni stock, ni vente sur 12 mois, ni commande, ni besoin prévu.' },
};
const STATUTS = [
  { code: 'commande', label: 'Commandé' },
  { code: 'production', label: 'En production' },
  { code: 'flottant', label: 'Flottant' },
  { code: 'livre', label: 'Livré' },
];

function erreurDe(e) { return e?.response?.data?.error || e?.message || 'Erreur'; }

const ONGLETS = [['couverture', '📊 Couverture'], ['previsions', '✍️ Prévisions'], ['commandes', '📦 Commandes'], ['calendrier', '🗓️ Calendrier'], ['parametres', '⚙️ Paramètres']];
// Filtre de la barre d'outils, propre à chaque onglet (le premier = défaut).
const FILTRES = {
  previsions: [['actives', 'Avec activité'], ['a_saisir', 'À saisir'], ['tout', 'Toutes les versions']],
  couverture: [['actives', 'Avec activité'], ['action', 'À commander'], ['rupture', 'En rupture'], ['surstock', 'En surstock'], ['tout', 'Toutes les versions']],
  commandes: [['en_cours', 'En cours'], ['terminees', 'Terminées'], ['tout', 'Toutes les commandes']],
};
const contient = (q, ...champs) => !q || champs.some((c) => String(c || '').toLowerCase().includes(q));

export default function CommandesVN({ user, setHeaderAction, editMode }) {
  const [perimetres, setPerimetres] = useState(null);
  const [concessionId, setConcessionId] = useState(() => Number(localStorage.getItem('cvn-concession')) || null);
  const [marque, setMarque] = useState(() => localStorage.getItem('cvn-marque') || '');
  const [onglet, setOnglet] = useState('couverture');
  const [commandeAOuvrir, setCommandeAOuvrir] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState(FILTRES.couverture[0][0]);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    api.perimetres().then((r) => {
      const ps = r.data.perimetres || [];
      setPerimetres(ps);
      const p = ps.find((x) => x.concession_id === concessionId) || ps[0];
      if (p && p.concession_id !== concessionId) setConcessionId(p.concession_id);
      if (p && !p.marques.includes(marque)) setMarque(p.suivies[0] || p.marques[0]);
    }).catch((e) => setErreur(erreurDe(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { try { if (concessionId) localStorage.setItem('cvn-concession', String(concessionId)); if (marque) localStorage.setItem('cvn-marque', marque); } catch (e) { /* navigation privée */ } }, [concessionId, marque]);

  const perim = perimetres?.find((p) => p.concession_id === concessionId);
  const changerConcession = (id) => {
    const p = perimetres.find((x) => x.concession_id === Number(id));
    setConcessionId(p.concession_id);
    if (!p.marques.includes(marque)) setMarque(p.suivies[0] || p.marques[0]);
  };
  const allerA = (k) => { setOnglet(k); setFiltre(FILTRES[k]?.[0][0] || ''); setRecherche(''); };
  const q = recherche.trim().toLowerCase();
  const commun = { concessionId, marque, setHeaderAction, recherche: q, filtre };

  return (
    <div className="page-container">
      <div className="page-tabs">
        {ONGLETS.map(([k, l]) => <button key={k} className={onglet === k ? 'active' : ''} onClick={() => allerA(k)}>{l}</button>)}
      </div>

      <div className="page-toolbar">
        {onglet !== 'calendrier' && <input className="page-search" placeholder="🔍 Rechercher..." value={recherche} onChange={(e) => setRecherche(e.target.value)} />}
        {perimetres && perimetres.length > 1 && (
          <select className="page-select" value={concessionId || ''} onChange={(e) => changerConcession(e.target.value)}>
            {perimetres.map((p) => <option key={p.concession_id} value={p.concession_id}>{p.concession}</option>)}
          </select>
        )}
        {perim && (
          <select className="page-select" value={marque} onChange={(e) => setMarque(e.target.value)} title="Une marque « à paramétrer » n'a pas encore de versions de suivi">
            {perim.marques.map((m) => <option key={m} value={m}>{m}{perim.suivies.includes(m) ? '' : ' (à paramétrer)'}</option>)}
          </select>
        )}
        {FILTRES[onglet] && (
          <select className={`page-select${filtre !== FILTRES[onglet][0][0] ? ' active-filter' : ''}`} value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            {FILTRES[onglet].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
      </div>

      {erreur && <div className="status-error cvn-bandeau">{erreur}</div>}
      {perimetres && perimetres.length === 0 && <div className="empty-state">Aucune concession avec des ventes ou du stock VN dans votre périmètre.</div>}
      {perimetres === null && !erreur && <div className="empty-state">Chargement…</div>}

      {concessionId && marque && onglet === 'couverture' && <Couverture {...commun} allerA={allerA} setFiltre={setFiltre} ouvrirCommande={(id) => { setCommandeAOuvrir(id); allerA('commandes'); }} />}
      {concessionId && marque && onglet === 'previsions' && <Previsions {...commun} />}
      {concessionId && marque && onglet === 'commandes' && <Commandes {...commun} user={user} editMode={editMode} ouvrir={commandeAOuvrir} ouvert={() => setCommandeAOuvrir(null)} />}
      {concessionId && marque && onglet === 'calendrier' && <Calendrier {...commun} />}
      {concessionId && marque && onglet === 'parametres' && <Parametres {...commun} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COUVERTURE
// ═══════════════════════════════════════════════════════════════════════════
// Ordre d'urgence : ce qui doit être traité d'abord remonte en tête.
const rythme = (x) => (x <= 0 ? '0' : x < 1 ? '< 1' : String(Math.round(x)));
const moisArrondis = (x) => (x == null ? '—' : x <= 0 ? '0 mois' : x < 1 ? '< 1 mois' : `${Math.round(x)} mois`);

function Couverture({ concessionId, marque, allerA, recherche, filtre, setFiltre, setHeaderAction, ouvrirCommande }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouverte, setOuverte] = useState(null);
  const [preparer, setPreparer] = useState(false);

  useEffect(() => {
    if (setHeaderAction) setHeaderAction({ primary: { label: '🧾 Préparer la commande', onClick: () => setPreparer(true) } });
  }, [setHeaderAction]);
  const charger = useCallback(() => {
    setErreur('');
    api.couverture(concessionId, marque).then((r) => setData(r.data)).catch((e) => { setErreur(erreurDe(e)); setData(null); });
  }, [concessionId, marque]);
  useEffect(() => { setData(null); setOuverte(null); charger(); }, [charger]);

  // Groupes par modèle et versions dans l'ORDRE FIXE des versions suivies
  // (Paramètres ▸ Versions, ordre du fichier repris) : la vue ne bouge jamais
  // d'un jour à l'autre, l'urgence se lit à la couleur, pas à la position.
  const groupes = useMemo(() => {
    if (!data) return [];
    const g = new Map();
    for (const v of data.versions.filter((x) => x.actif)) {
      if (!g.has(v.modele)) g.set(v.modele, []);
      g.get(v.modele).push(v);
    }
    return [...g.entries()];
  }, [data]);

  if (erreur) return <div className="status-error cvn-bandeau">{erreur}</div>;
  if (!data) return <div className="empty-state">Chargement de la couverture…</div>;
  if (!data.versions.length) {
    return (
      <div className="empty-state">
        Aucune version de suivi pour {marque}.<br />
        <button type="button" className="btn-primary" style={{ marginTop: 12 }} onClick={() => allerA('parametres')}>Créer les versions</button>
      </div>
    );
  }

  const actives = data.versions.filter((v) => v.actif);
  const compte = (etat) => actives.filter((v) => v.etat === etat).length;
  const urgentes = actives.filter((v) => ['rupture', 'a_commander'].includes(v.etat) && v.a_commander > 0);
  const aCommander = urgentes.reduce((s, v) => s + v.a_commander, 0);
  const montant = urgentes.reduce((s, v) => s + v.a_commander * (Number(v.prix_achat_ht) || 0), 0);
  const sansPrix = urgentes.filter((v) => !(Number(v.prix_achat_ht) > 0)).length;
  const p = data.parametres;
  const garde = (v) => {
    if (!contient(recherche, v.libelle, v.modele)) return false;
    if (filtre === 'tout') return true;
    if (filtre === 'action') return ['rupture', 'a_commander'].includes(v.etat);
    if (filtre === 'rupture' || filtre === 'surstock' || filtre === 'a_saisir') return v.etat === filtre;
    return v.etat !== 'inactif';
  };
  const aClasser = data.a_classer.filter((a) => a.ventes_24m + a.stock > 0);
  const commandes = (v) => (Number(v.commande) || 0) + (Number(v.en_production) || 0) + (Number(v.flottant) || 0);
  const kpi = (cle, libelle, valeur, detail, classe) => (
    <button type="button" className={`cvn-kpi cvn-kpi--clic${classe ? ` ${classe}` : ''}${filtre === cle ? ' actif' : ''}`}
      onClick={() => setFiltre(filtre === cle ? 'actives' : cle)} title={filtre === cle ? 'Retirer le filtre' : 'Filtrer le tableau'}>
      <span className="l">{libelle}</span><span className="v">{valeur}</span><span className="d">{detail}</span>
    </button>
  );

  return (
    <>
      <div className="cvn-kpis">
        {kpi('action', 'À commander', nf.format(aCommander), `véhicules · ${aCommander ? `≈ ${keur(montant)} HT${sansPrix ? ` (${sansPrix} version${sansPrix > 1 ? 's' : ''} sans prix)` : ''}` : 'rien à commander'}`, aCommander > 0 ? 'cvn-kpi--alerte' : '')}
        {kpi('rupture', 'En rupture', compte('rupture'), 'versions', compte('rupture') > 0 ? 'cvn-kpi--rupture' : '')}
        {kpi('surstock', 'En surstock', compte('surstock'), 'versions', '')}
        {compte('a_saisir') > 0 && kpi('a_saisir', 'Prévisions à saisir', compte('a_saisir'), 'versions sans prévision complète', 'cvn-kpi--saisir')}
      </div>

      <div className="cvn-infos">
        <span>Délai de commande <b>{String(p.delai_commande_mois).replace('.', ',')} mois</b> + couverture voulue <b>{String(p.couverture_cible_mois).replace('.', ',')} mois</b>
          {!p.configure && <> · <button type="button" className="cvn-lien" onClick={() => allerA('parametres')}>à régler</button></>}</span>
        {compte('a_saisir') > 0 && <button type="button" className="cvn-lien" onClick={() => allerA('previsions')}>✍️ Saisir les prévisions</button>}
        {aClasser.length > 0 && <button type="button" className="cvn-lien" onClick={() => allerA('parametres')}>⚠️ {aClasser.length} libellé{aClasser.length > 1 ? 's' : ''} DMS à classer</button>}
        {data.correspondances_proposees > 0 && <button type="button" className="cvn-lien" onClick={() => allerA('parametres')}>🔎 {data.correspondances_proposees} correspondances à vérifier</button>}
        {data.alertes.eta_depassees > 0 && <button type="button" className="cvn-lien" onClick={() => allerA('commandes')}>⏰ {data.alertes.eta_depassees} ETA dépassée{data.alertes.eta_depassees > 1 ? 's' : ''}</button>}
      </div>

      <div className="table-container cvn-scroll">
        <table className="cvn-table cvn-couv">
          <thead>
            <tr>
              <th className="l">Version</th>
              <th title="Ventes nettes moyennes par mois, sur les 6 derniers mois">Vend / mois</th>
              <th title="Stock VN dans le DMS (la veille)">Stock</th>
              <th title="Commandés non arrivés : commandé + en production + flottant">Commandes</th>
              <th title="(Stock + commandes) / ventes prévues par mois">Couverture</th>
              <th>À commander</th>
              <th className="l">État</th>
            </tr>
          </thead>
          <tbody>
            {groupes.map(([modele, vs]) => {
              const affich = vs.filter(garde);
              if (!affich.length) return null;
              return (
                <React.Fragment key={modele}>
                  <tr className="cvn-groupe"><td className="l" colSpan={7}>{modele}</td></tr>
                  {affich.map((v) => (
                    <React.Fragment key={v.id}>
                      <tr className={`cvn-ligne${ouverte === v.id ? ' ouverte' : ''}`} onClick={() => setOuverte(ouverte === v.id ? null : v.id)}>
                        <td className="l"><span className="cvn-chevron">{ouverte === v.id ? '▾' : '▸'}</span>{v.libelle}{v.delai_propre && <span className="inline-tag inline-tag-muted" title="Délai de commande propre à cette version">{String(v.delai_commande_mois).replace('.', ',')} mois</span>}</td>
                        <td>{rythme(v.vmm6)}</td>
                        <td title={v.stock_vd > 0 ? `+ ${v.stock_vd} VD (non compté)` : ''}>{v.stock || '—'}</td>
                        <td title={`Commandé : ${v.commande || 0} · En production : ${v.en_production || 0} · Flottant : ${v.flottant || 0}`}>{commandes(v) || '—'}</td>
                        <td>{moisArrondis(v.couverture_mois)}</td>
                        <td className={v.etat === 'a_saisir' ? 'cvn-muet' : 'cvn-acommander'} title={v.etat === 'a_saisir' ? `${v.previsions_manquantes} mois sans prévision : pas de calcul tant que la prévision n'est pas complète` : ''}>{v.etat === 'a_saisir' ? '?' : v.a_commander > 0 ? v.a_commander : '—'}</td>
                        <td className="l"><span className={ETATS[v.etat].classe} title={ETATS[v.etat].aide}>{ETATS[v.etat].label}</span></td>
                      </tr>
                      {ouverte === v.id && (
                        <tr className="cvn-detail"><td colSpan={7}><DetailVersion v={v} onChange={charger} /></td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {preparer && <PreparerCommande data={data} concessionId={concessionId} marque={marque} fermer={() => setPreparer(false)} creee={(id) => { setPreparer(false); ouvrirCommande(id); }} />}
    </>
  );
}

// « Préparer la commande » : les quantités à commander préremplies et
// modifiables, le montant, et un clic crée le dossier (lots « Commandé »,
// ETA = aujourd'hui + délai de commande de la version).
function PreparerCommande({ data, concessionId, marque, fermer, creee }) {
  const ajd = aujourdhui();
  const etaDe = (v) => { const d = new Date(`${ajd}T12:00:00`); d.setMonth(d.getMonth() + Math.round(v.delai_commande_mois)); return d.toISOString().slice(0, 10); };
  // Même ordre fixe que la couverture.
  const versions = useMemo(() => data.versions.filter((v) => v.actif && v.etat !== 'inactif'), [data]);
  const [lignes, setLignes] = useState(() => Object.fromEntries(versions.map((v) => {
    const suggere = ['rupture', 'a_commander'].includes(v.etat) && v.a_commander > 0;
    return [v.id, { coche: suggere, quantite: suggere ? v.a_commander : 0, prix: v.prix_achat_ht ?? '', eta: etaDe(v) }];
  })));
  const [toutes, setToutes] = useState(false);
  const [entete, setEntete] = useState({ reference: `CMD-${marque}-${ajd.slice(0, 7)}`, fournisseur: '', date_commande: ajd });
  const [envoi, setEnvoi] = useState(false);
  const maj = (id, champs) => setLignes((l) => ({ ...l, [id]: { ...l[id], ...champs } }));
  const visibles = versions.filter((v) => toutes || v.a_commander > 0 || lignes[v.id].coche);
  const retenues = versions.filter((v) => lignes[v.id].coche && Number(lignes[v.id].quantite) > 0);
  const total = retenues.reduce((s, v) => s + Number(lignes[v.id].quantite), 0);
  const montant = retenues.reduce((s, v) => s + Number(lignes[v.id].quantite) * (Number(lignes[v.id].prix) || 0), 0);
  const creer = async () => {
    setEnvoi(true);
    try {
      const { data: r } = await api.creerCommande(concessionId, marque, {
        ...entete,
        lots: retenues.map((v) => ({ version_id: v.id, quantite: Number(lignes[v.id].quantite), prix_unitaire_ht: lignes[v.id].prix, eta: lignes[v.id].eta || null })),
      });
      creee(r.commande.id);
    } catch (e) { alert(erreurDe(e)); setEnvoi(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content large cvn-dossier" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🧾 Préparer la commande {marque}</h2>
          <button type="button" className="modal-close" onClick={fermer}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-grid cvn-prep-entete">
            <div className="form-group"><label>Référence *</label><input value={entete.reference} onChange={(e) => setEntete({ ...entete, reference: e.target.value })} /></div>
            <div className="form-group"><label>Fournisseur</label><input value={entete.fournisseur} placeholder="ex. ITC / Inchcape" onChange={(e) => setEntete({ ...entete, fournisseur: e.target.value })} /></div>
            <div className="form-group"><label>Date de commande</label><input type="date" value={entete.date_commande} onChange={(e) => setEntete({ ...entete, date_commande: e.target.value })} /></div>
          </div>
          <p className="cvn-regle">Quantités proposées = colonne « À commander » de la couverture. Modifiez-les librement ; l'ETA proposée est aujourd'hui + le délai de commande. Les lots sont créés au statut « Commandé ».</p>
          <div className="table-container cvn-scroll">
            <table className="cvn-table">
              <thead>
                <tr><th className="l" /><th className="l">Version</th><th className="l">État</th><th>Proposé</th><th>Quantité</th><th>Prix unit. HT</th><th>Montant HT</th><th>ETA</th></tr>
              </thead>
              <tbody>
                {visibles.map((v) => {
                  const l = lignes[v.id];
                  return (
                    <tr key={v.id} className={l.coche ? '' : 'cvn-livre'}>
                      <td className="l"><input type="checkbox" checked={l.coche} onChange={(e) => maj(v.id, { coche: e.target.checked, quantite: e.target.checked && !Number(l.quantite) ? Math.max(1, v.a_commander) : l.quantite })} /></td>
                      <td className="l">{v.libelle}</td>
                      <td className="l"><span className={ETATS[v.etat].classe}>{ETATS[v.etat].label}</span></td>
                      <td>{v.a_commander || '—'}</td>
                      <td><input className="cvn-num" type="number" min="0" value={l.quantite} onChange={(e) => maj(v.id, { quantite: e.target.value, coche: Number(e.target.value) > 0 })} /></td>
                      <td><input className="cvn-prix" type="number" min="0" step="100" value={l.prix} onChange={(e) => maj(v.id, { prix: e.target.value })} /></td>
                      <td className="cvn-fort">{l.coche && Number(l.quantite) > 0 ? eur(Number(l.quantite) * (Number(l.prix) || 0)) : ''}</td>
                      <td><input type="date" value={l.eta} onChange={(e) => maj(v.id, { eta: e.target.value })} /></td>
                    </tr>
                  );
                })}
                {visibles.length === 0 && <tr><td colSpan={8} className="empty-state">Rien à commander d'après la couverture. Affichez toutes les versions pour commander quand même.</td></tr>}
              </tbody>
            </table>
          </div>
          <button type="button" className="cvn-lien cvn-prep-toutes" onClick={() => setToutes(!toutes)}>{toutes ? 'Ne montrer que les versions proposées' : `Afficher toutes les versions (${versions.length})`}</button>
        </div>
        <div className="modal-footer">
          <span className="cvn-footer-gauche cvn-prep-total"><b>{nf.format(total)}</b> véhicule{total > 1 ? 's' : ''} · <b>{eur(montant)}</b> HT{retenues.some((v) => !(Number(lignes[v.id].prix) > 0)) ? ' (prix manquants)' : ''}</span>
          <button type="button" className="btn-secondary" onClick={fermer}>Annuler</button>
          <button type="button" className="btn-primary" disabled={envoi || !total || !entete.reference.trim()} onClick={creer}>Créer la commande</button>
        </div>
      </div>
    </div>
  );
}

// Détail d'une version : une frise mois par mois (24 mois réels, le mois en
// cours, 12 mois de prévision) avec axe des mois et des années, repère
// « aujourd'hui », période comptée dans le besoin surlignée, et la case de
// chaque prévision sous sa propre barre. Ventes réelles chiffrées sur les
// barres ; prévisions chiffrées dans leur case.
const INITIALES = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
function DetailVersion({ v, onChange }) {
  const [enCours, setEnCours] = useState(null);
  const ajuster = async (ym, valeur) => {
    setEnCours(ym);
    try { await api.ajusterPrevision(v.id, ym, valeur === '' ? null : Number(valeur)); onChange(); }
    catch (e) { alert(erreurDe(e)); } finally { setEnCours(null); }
  };
  const ventesParMois = new Map(v.ventes.map((x) => [x.ym, x.n]));
  const ilYaUnAn = (ym) => { const [a, m] = ym.split('-'); return ventesParMois.get(`${Number(a) - 1}-${m}`); };
  const nbPasse = v.ventes.length;              // 24 mois clos + le mois en cours
  const horizon = v.horizon_besoin_mois;
  const cols = [
    ...v.ventes.map((x, i) => ({ ym: x.ym, n: Math.max(0, x.n), type: i === nbPasse - 1 ? 'encours' : 'reel' })),
    ...v.previsions.map((x, i) => ({ ym: x.ym, n: x.quantite || 0, vide: x.quantite == null, type: 'prev', horizon: i < horizon })),
  ];
  const max = Math.max(1, ...cols.map((c) => c.n));
  const graduation = max <= 5 ? max : Math.ceil(max / 5) * 5; // échelle ronde
  const grille = { gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` };
  const debutHorizon = nbPasse + 1;            // colonnes CSS (1-indexées)
  const annees = cols.reduce((acc, c, i) => {
    if (i === 0 || c.ym.endsWith('-01')) acc.push({ annee: c.ym.slice(0, 4), debut: i + 1 });
    return acc;
  }, []);
  const plageHorizon = v.previsions.length ? `${moisLib(v.previsions[0].ym)} à ${moisLib(v.previsions[Math.max(0, horizon - 1)].ym)}` : '';

  return (
    <div className="cvn-detail-corps">
      <div className="cvn-synthese">
        <div><span className={`v${v.previsions_manquantes ? ' cvn-negatif' : ''}`}>{v.previsions_manquantes ? '?' : v.besoin}</span><span className="l">ventes prévues sur {horizon} mois</span><span className="d">{v.previsions_manquantes ? `${v.previsions_manquantes} mois à saisir (${plageHorizon})` : `${plageHorizon} (délai de commande + couverture voulue)`}</span></div>
        <div><span className="v">{v.ressources}</span><span className="l">stock + commandes</span><span className="d">{v.stock} en stock · {(v.commande || 0) + (v.en_production || 0) + (v.flottant || 0)} commandés</span></div>
        <div><span className={`v${v.stock_projete < 0 ? ' cvn-negatif' : ''}`}>{v.previsions_manquantes ? '?' : v.stock_projete}</span><span className="l">stock dans {Math.ceil(v.delai_commande_mois)} mois</span><span className="d">sans nouvelle commande (= quand arriverait une commande passée aujourd'hui)</span></div>
      </div>

      <div className="cvn-frise" style={grille}>
        {/* Bandeau : réel | prévisions, et la période comptée dans le besoin */}
        <div className="cvn-f-zone reel" style={{ gridColumn: `1 / ${nbPasse + 1}` }}>Ventes nettes réelles</div>
        {horizon > 0
          ? <div className="cvn-f-horizon" style={{ gridColumn: `${debutHorizon} / ${debutHorizon + horizon}` }} title="Ventes prévues sur le délai de commande + la couverture voulue">Prévisions · besoin {horizon} mois : {v.previsions_manquantes ? `${v.previsions_manquantes} mois à saisir` : v.besoin}</div>
          : <div className="cvn-f-zone prev" style={{ gridColumn: `${nbPasse + 1} / -1` }}>Prévisions</div>}

        {/* Barres */}
        <div className="cvn-f-echelle" style={{ gridColumn: '1 / -1' }}>
          <span style={{ bottom: '100%' }}>{graduation}</span>
          <span style={{ bottom: '50%' }}>{Math.round(graduation / 2)}</span>
        </div>
        {cols.map((c, i) => (
          <div key={c.ym} className={`cvn-f-col ${c.type}${c.horizon ? ' horizon' : ''}${c.vide ? ' vide' : ''}${i === nbPasse - 1 ? ' aujourdhui' : ''}`}
            style={{ gridColumn: i + 1 }}
            title={c.type === 'prev'
              ? `${moisLib(c.ym)} : ${c.vide ? 'prévision à saisir' : `${c.n} prévu${c.n > 1 ? 's' : ''}`}${ilYaUnAn(c.ym) != null ? ` · même mois l'an dernier : ${ilYaUnAn(c.ym)}` : ''}`
              : `${moisLib(c.ym)} : ${c.n} vente${c.n > 1 ? 's' : ''} nette${c.n > 1 ? 's' : ''}${c.type === 'encours' ? ' (mois en cours, incomplet)' : ''}`}>
            {c.type !== 'prev' && c.n > 0 && <em>{c.n}</em>}
            {c.vide && <em className="cvn-f-vide">?</em>}
            <span style={{ height: `${(c.n / graduation) * 100}%` }} />
          </div>
        ))}

        {/* Axe : initiale du mois, puis année */}
        {cols.map((c, i) => (
          <div key={`m${c.ym}`} className={`cvn-f-mois${c.ym.endsWith('-01') ? ' janvier' : ''}${i === nbPasse - 1 ? ' aujourdhui' : ''}`} style={{ gridColumn: i + 1 }}>
            {i === nbPasse - 1 ? 'auj.' : INITIALES[Number(c.ym.slice(5)) - 1]}
          </div>
        ))}
        {annees.map((a, k) => (
          <div key={a.annee + a.debut} className="cvn-f-annee" style={{ gridColumn: `${a.debut} / ${annees[k + 1] ? annees[k + 1].debut : cols.length + 1}` }}>{a.annee}</div>
        ))}

        {/* Cases de prévision, sous leur barre */}
        <div className="cvn-f-saisie-lib" style={{ gridColumn: `1 / ${nbPasse + 1}` }}>Prévision du chef des ventes : saisissez la quantité sous chaque mois →</div>
        {v.previsions.map((x, i) => (
          <input key={`p${x.ym}-${x.quantite}`} className={`cvn-f-saisie${x.quantite == null && i < horizon ? ' manquante' : ''}`} style={{ gridColumn: nbPasse + i + 1 }}
            type="number" min="0" defaultValue={x.quantite ?? ''} placeholder="?" disabled={enCours === x.ym}
            title={`${moisLib(x.ym)} — même mois l'an dernier : ${x.n1}`}
            onBlur={(e) => { const val = e.target.value; if (val === '' ? x.quantite != null : Number(val) !== x.quantite) ajuster(x.ym, val); }} />
        ))}
      </div>

      <div className="cvn-histo-legende">
        <span className="cvn-l-reel" /> ventes réelles
        <span className="cvn-l-prev" /> prévision du chef des ventes
        {v.derniere_saisie && <span className="cvn-l-rien">— saisie par {v.derniere_saisie.par || '—'} le {dateFr(v.derniere_saisie.le)}</span>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PRÉVISIONS — l'engagement du chef des ventes, saisi par version et par mois
// (comme l'onglet « VENTES PRÉVISIONNELLES » du fichier). Aucune valeur
// calculée : une case vide reste vide et bloque le calcul « à commander ».
// ═══════════════════════════════════════════════════════════════════════════
function Previsions({ concessionId, marque, recherche, filtre, setHeaderAction }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);
  const charger = useCallback(() => {
    api.couverture(concessionId, marque).then((r) => setData(r.data)).catch((e) => setErreur(erreurDe(e)));
  }, [concessionId, marque]);
  useEffect(() => { setData(null); charger(); }, [charger]);

  const groupes = useMemo(() => {
    if (!data) return [];
    const g = new Map();
    for (const v of data.versions.filter((x) => x.actif)) {
      if (!g.has(v.modele)) g.set(v.modele, []);
      g.get(v.modele).push(v);
    }
    return [...g.entries()];
  }, [data]);

  if (erreur) return <div className="status-error cvn-bandeau">{erreur}</div>;
  if (!data) return <div className="empty-state">Chargement des prévisions…</div>;
  const actives = data.versions.filter((v) => v.actif);
  if (!actives.length) return <div className="empty-state">Aucune version suivie pour {marque} : créez-les dans Paramètres.</div>;

  const mois = actives[0].previsions.map((x) => x.ym);
  const p = data.parametres;
  const horizon = Math.min(mois.length, Math.ceil(Number(p.delai_commande_mois) + Number(p.couverture_cible_mois)));
  const garde = (v) => contient(recherche, v.libelle, v.modele)
    && (filtre === 'tout' || (filtre === 'a_saisir' ? v.etat === 'a_saisir' : v.etat !== 'inactif'));
  const val = (v, ym) => v.previsions.find((x) => x.ym === ym)?.quantite;
  const somme = (vs, ym) => vs.reduce((s, v) => s + (val(v, ym) || 0), 0);
  const total12 = (v) => v.previsions.reduce((s, x) => s + (x.quantite || 0), 0);
  const aSaisir = actives.filter((v) => v.etat === 'a_saisir').length;
  const derniere = actives.map((v) => v.derniere_saisie).filter(Boolean).sort((a, b) => new Date(b.le) - new Date(a.le))[0];
  const saisir = async (v, ym, valeur) => {
    try { await api.ajusterPrevision(v.id, ym, valeur === '' ? null : Number(valeur)); charger(); }
    catch (e) { alert(erreurDe(e)); }
  };

  return (
    <>
      <div className="cvn-infos">
        <span>Engagement du chef des ventes : ventes prévues <b>par version et par mois</b>. Les mois surlignés ({moisLib(mois[0])} → {moisLib(mois[horizon - 1])}) entrent dans le calcul des commandes.</span>
        {aSaisir > 0 ? <span className="cvn-a-saisir">⚠️ {aSaisir} version{aSaisir > 1 ? 's' : ''} sans prévision complète</span> : <span>✅ Prévisions complètes</span>}
        {derniere && <span>Dernière saisie : <b>{derniere.par || '—'}</b>, le {dateFr(derniere.le)}</span>}
      </div>
      <div className="table-container cvn-scroll">
        <table className="cvn-table cvn-previsions">
          <thead>
            <tr>
              <th className="l">Version</th>
              {mois.map((ym, i) => <th key={ym} className={i < horizon ? 'horizon' : ''}>{moisLib(ym)}</th>)}
              <th className="cvn-cal-total">Total 12 mois</th>
            </tr>
          </thead>
          <tbody>
            {groupes.map(([modele, vs]) => {
              const affich = vs.filter(garde);
              if (!affich.length) return null;
              return (
                <React.Fragment key={modele}>
                  <tr className="cvn-groupe">
                    <td className="l">{modele}</td>
                    {mois.map((ym, i) => <td key={ym} className={i < horizon ? 'horizon' : ''}>{somme(vs, ym) || ''}</td>)}
                    <td className="cvn-cal-total">{vs.reduce((s, v) => s + total12(v), 0) || ''}</td>
                  </tr>
                  {affich.map((v) => (
                    <tr key={v.id}>
                      <td className="l">{v.libelle}</td>
                      {v.previsions.map((x, i) => (
                        <td key={x.ym} className={i < horizon ? 'horizon' : ''}>
                          <input key={`${x.ym}-${x.quantite}`} type="number" min="0"
                            className={`cvn-prev-case${x.quantite == null && i < horizon ? ' manquante' : ''}`}
                            defaultValue={x.quantite ?? ''} placeholder={i < horizon ? '?' : ''}
                            title={`${moisLib(x.ym)} — même mois l'an dernier : ${x.n1} vente${x.n1 > 1 ? 's' : ''}`}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            onBlur={(e) => { const s = e.target.value; if (s === '' ? x.quantite != null : Number(s) !== x.quantite) saisir(v, x.ym, s); }} />
                        </td>
                      ))}
                      <td className="cvn-cal-total">{total12(v) || ''}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="l">Total {marque}</td>
              {mois.map((ym, i) => <td key={ym} className={i < horizon ? 'horizon' : ''}>{somme(actives, ym) || ''}</td>)}
              <td className="cvn-cal-total">{actives.reduce((s, v) => s + total12(v), 0) || ''}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="cvn-regle">Saisissez 0 pour un mois sans vente prévue : une case vide signifie « pas encore saisi » et bloque le calcul de la commande. Au survol d'une case : les ventes du même mois l'an dernier.</p>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMMANDES
// ═══════════════════════════════════════════════════════════════════════════
function Commandes({ concessionId, marque, user, editMode, recherche, filtre, setHeaderAction, ouvrir, ouvert }) {
  const [liste, setListe] = useState(null);
  const [ouverte, setOuverte] = useState(ouvrir || null);
  const [nouvelle, setNouvelle] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [erreur, setErreur] = useState('');
  const peutSupprimer = editMode && user?.role === 'super-admin';

  useEffect(() => {
    if (setHeaderAction) setHeaderAction({ primary: { label: '+ Nouvelle commande', onClick: () => setNouvelle({ reference: '', fournisseur: '', date_commande: aujourdhui() }) } });
  }, [setHeaderAction]);
  const charger = useCallback(() => {
    api.commandes(concessionId, marque).then((r) => setListe(r.data.commandes)).catch((e) => setErreur(erreurDe(e)));
  }, [concessionId, marque]);
  useEffect(() => { setListe(null); setSelection(new Set()); charger(); }, [charger]);
  useEffect(() => { if (ouvrir && ouvert) ouvert(); }, [ouvrir, ouvert]); // ouverte une fois, à l'arrivée
  useEffect(() => { if (!peutSupprimer) setSelection(new Set()); }, [peutSupprimer]);

  const creer = async () => {
    try {
      const { data } = await api.creerCommande(concessionId, marque, nouvelle);
      setNouvelle(null); charger(); setOuverte(data.commande.id);
    } catch (e) { alert(erreurDe(e)); }
  };
  const supprimer = async (ids) => {
    const cmds = (liste || []).filter((c) => ids.includes(c.id));
    if (!window.confirm(ids.length === 1 ? `Supprimer la commande ${cmds[0]?.reference} et tous ses lots ?` : `Supprimer ${ids.length} commandes et tous leurs lots ?`)) return;
    try { for (const id of ids) await api.supprimerCommande(id); } catch (e) { alert(erreurDe(e)); }
    setSelection(new Set()); charger();
  };

  const termine = (c) => c.quantite > 0 && c.livres >= c.quantite;
  const affich = (liste || []).filter((c) => contient(recherche, c.reference, c.fournisseur)
    && (filtre === 'tout' || (filtre === 'terminees' ? termine(c) : !termine(c))));
  const basculer = (id) => setSelection((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const tousCoches = affich.length > 0 && affich.every((c) => selection.has(c.id));

  return (
    <>
      {erreur && <div className="status-error cvn-bandeau">{erreur}</div>}
      {peutSupprimer && selection.size > 0 && (
        <div className="page-bulk-bar">
          <span>{selection.size} sélectionnée{selection.size > 1 ? 's' : ''}</span>
          <button type="button" className="btn-secondary" onClick={() => supprimer([...selection])}>🗑️ Supprimer la sélection</button>
        </div>
      )}
      <div className="table-container cvn-scroll">
        <table className="cvn-table">
          <thead>
            <tr>
              {peutSupprimer && <th className="l"><input type="checkbox" checked={tousCoches} onChange={() => setSelection(tousCoches ? new Set() : new Set(affich.map((c) => c.id)))} /></th>}
              <th className="l">Référence</th>
              <th className="l">Fournisseur</th>
              <th>Commandée le</th>
              <th className="l">Livrés</th>
              <th>Flottant</th>
              <th>Prochaine ETA</th>
              <th>Montant HT</th>
              <th>Reste à payer</th>
              <th>Prochain paiement</th>
              <th className="l">Actions</th>
            </tr>
          </thead>
          <tbody>
            {liste === null && <tr><td colSpan={11} className="empty-state">Chargement…</td></tr>}
            {liste && affich.length === 0 && <tr><td colSpan={11} className="empty-state">{liste.length ? 'Aucune commande ne correspond aux filtres.' : `Aucune commande pour ${marque}.`}</td></tr>}
            {affich.map((c) => {
              const pct = c.quantite ? Math.round((c.livres / c.quantite) * 100) : 0;
              return (
                <tr key={c.id} className={`cvn-ligne${termine(c) ? ' cvn-livre' : ''}`} onClick={() => setOuverte(c.id)}>
                  {peutSupprimer && <td className="l" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selection.has(c.id)} onChange={() => basculer(c.id)} /></td>}
                  <td className="l"><b>{c.reference}</b></td>
                  <td className="l">{c.fournisseur || '—'}</td>
                  <td>{dateFr(c.date_commande)}</td>
                  <td className="l"><div className="cvn-livres"><div className="cvn-progression"><span style={{ width: `${pct}%` }} /></div><span>{c.livres}/{c.quantite}</span></div></td>
                  <td>{c.flottant || '—'}</td>
                  <td>{c.eta_depassees > 0 && <span className="badge-pill badge-warning cvn-badge-avant" title={`${c.eta_depassees} lot${c.eta_depassees > 1 ? 's' : ''} dont l'ETA est passée : sont-ils arrivés ?`}>⏰ {c.eta_depassees}</span>}{!termine(c) && c.prochaine_eta ? dateFr(c.prochaine_eta) : '—'}</td>
                  <td>{keur(Number(c.montant_ht))}</td>
                  <td className={Number(c.reste_a_payer) > 0 ? 'cvn-fort' : ''}>{Number(c.reste_a_payer) > 0 ? keur(Number(c.reste_a_payer)) : '—'}</td>
                  <td>{Number(c.reste_a_payer) > 0 && c.prochain_paiement ? dateFr(c.prochain_paiement) : '—'}</td>
                  <td className="l" onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button type="button" className="btn-icon" title="Ouvrir" onClick={() => setOuverte(c.id)}>👁️</button>
                      {peutSupprimer && <button type="button" className="btn-icon delete" title="Supprimer" onClick={() => supprimer([c.id])}>🗑️</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {nouvelle && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle commande {marque}</h2>
              <button type="button" className="modal-close" onClick={() => setNouvelle(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full"><label>Référence *</label><input value={nouvelle.reference} autoFocus onChange={(e) => setNouvelle({ ...nouvelle, reference: e.target.value })} placeholder="Commande XYZ" /></div>
                <div className="form-group"><label>Fournisseur</label><input value={nouvelle.fournisseur} onChange={(e) => setNouvelle({ ...nouvelle, fournisseur: e.target.value })} placeholder="ex. ITC / Inchcape" /></div>
                <div className="form-group"><label>Date de commande</label><input type="date" value={nouvelle.date_commande} onChange={(e) => setNouvelle({ ...nouvelle, date_commande: e.target.value })} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setNouvelle(null)}>Annuler</button>
              <button type="button" className="btn-primary" disabled={!nouvelle.reference.trim()} onClick={creer}>Créer</button>
            </div>
          </div>
        </div>
      )}
      {ouverte && <DossierCommande id={ouverte} fermer={() => { setOuverte(null); charger(); }} concessionId={concessionId} marque={marque} peutSupprimer={peutSupprimer} />}
    </>
  );
}

// Dossier d'une commande (modale) : en-tête modifiable + lots éditables en place.
function DossierCommande({ id, fermer, concessionId, marque, peutSupprimer }) {
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [ajout, setAjout] = useState(null);
  const [entete, setEntete] = useState(null);
  const [frac, setFrac] = useState(null);

  const charger = useCallback(() => {
    api.commande(id).then((r) => setData(r.data)).catch((e) => alert(erreurDe(e)));
  }, [id]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    api.couverture(concessionId, marque).then((r) => setVersions(r.data.versions.filter((v) => v.actif))).catch(() => setVersions([]));
  }, [concessionId, marque]);

  const commande = data?.commande;
  const lots = data?.lots || [];
  const total = lots.reduce((s, l) => s + l.quantite, 0);
  const livres = lots.reduce((s, l) => s + (l.statut === 'livre' ? l.quantite : Math.min(l.quantite, l.livres_constates)), 0);
  const montant = lots.reduce((s, l) => s + Number(l.montant_ht || 0), 0);
  const reste = lots.filter((l) => !l.paye).reduce((s, l) => s + Number(l.montant_ht || 0), 0);

  const maj = async (lot, champs) => {
    try { await api.modifierLot(lot.id, champs); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const fractionner = async () => {
    try { await api.fractionnerLot(frac.lot.id, Number(frac.quantite), frac.eta || null); setFrac(null); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const supprimerLot = async (lot) => {
    if (!window.confirm(`Supprimer le lot ${lot.quantite} × ${lot.version} ?`)) return;
    try { await api.supprimerLot(lot.id); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const ajouter = async () => {
    try { await api.ajouterLot(id, ajout); setAjout(null); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const enregistrerEntete = async () => {
    try { await api.modifierCommande(id, entete); setEntete(null); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const supprimer = async () => {
    if (!window.confirm(`Supprimer la commande ${commande.reference} et ses ${lots.length} lots ?`)) return;
    try { await api.supprimerCommande(id); fermer(); } catch (e) { alert(erreurDe(e)); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large cvn-dossier" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📦 {commande ? commande.reference : 'Commande'}</h2>
          <button type="button" className="modal-close" onClick={fermer}>✕</button>
        </div>
        <div className="modal-body">
          {!data ? <div className="empty-state">Chargement…</div> : (
            <>
              <div className="cvn-dossier-h">
                {!entete ? (
                  <div>
                    <div className="cvn-dossier-meta">
                      <span>{commande.fournisseur || 'Fournisseur non renseigné'}</span>
                      {commande.date_commande && <span>commandée le {dateFr(commande.date_commande)}</span>}
                      <button type="button" className="btn-icon" title="Modifier" onClick={() => setEntete({ reference: commande.reference, fournisseur: commande.fournisseur || '', date_commande: isoJour(commande.date_commande), commentaire: commande.commentaire || '' })}>✏️</button>
                    </div>
                    {commande.commentaire && <p className="cvn-dossier-com">{commande.commentaire}</p>}
                  </div>
                ) : (
                  <div className="form-grid cvn-entete-form">
                    <div className="form-group"><label>Référence</label><input value={entete.reference} onChange={(e) => setEntete({ ...entete, reference: e.target.value })} /></div>
                    <div className="form-group"><label>Fournisseur</label><input value={entete.fournisseur} onChange={(e) => setEntete({ ...entete, fournisseur: e.target.value })} /></div>
                    <div className="form-group"><label>Date de commande</label><input type="date" value={entete.date_commande} onChange={(e) => setEntete({ ...entete, date_commande: e.target.value })} /></div>
                    <div className="form-group full"><label>Commentaire</label><textarea rows={2} value={entete.commentaire} onChange={(e) => setEntete({ ...entete, commentaire: e.target.value })} /></div>
                    <div className="form-group full cvn-form-actions">
                      <button type="button" className="btn-secondary" onClick={() => setEntete(null)}>Annuler</button>
                      <button type="button" className="btn-primary" onClick={enregistrerEntete}>Enregistrer</button>
                    </div>
                  </div>
                )}
                <div className="cvn-dossier-kpis">
                  <div><b>{livres}<span className="cvn-sur">/{total}</span></b><small>livrés</small></div>
                  <div><b>{keur(montant)}</b><small>montant HT</small></div>
                  <div><b>{keur(reste)}</b><small>reste à payer</small></div>
                </div>
              </div>

              <div className="table-container cvn-scroll">
                <table className="cvn-table cvn-lots">
                  <thead>
                    <tr>
                      <th className="l">Version</th><th>Qté</th><th className="l">Statut</th><th>ETA</th>
                      <th title="N° de commande du constructeur : quand les véhicules arrivent dans le DMS avec ce numéro, le lot passe seul en Livré">N° constructeur</th>
                      <th>Prix unit. HT</th><th>Montant HT</th>
                      <th title="ETA + délai de paiement de la marque, tant qu'elle n'est pas saisie à la main">Paiement prévu</th>
                      <th className="l">Payé</th><th className="l">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lots.map((l) => {
                      const depassee = l.statut !== 'livre' && l.eta && isoJour(l.eta) < aujourdhui();
                      return (
                        <tr key={l.id} className={l.statut === 'livre' ? 'cvn-livre' : ''}>
                          <td className="l">{l.version}{l.livres_constates > 0 && l.statut !== 'livre' && <span className="inline-tag inline-tag-muted" title="Véhicules arrivés dans le DMS avec ce n° de commande">{l.livres_constates} arrivé{l.livres_constates > 1 ? 's' : ''}</span>}</td>
                          <td><input className="cvn-num" type="number" min="1" defaultValue={l.quantite} onBlur={(e) => Number(e.target.value) !== l.quantite && maj(l, { quantite: Number(e.target.value) })} /></td>
                          <td className="l">
                            <select value={l.statut} onChange={(e) => maj(l, { statut: e.target.value })}>
                              {STATUTS.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                            </select>
                          </td>
                          <td className={depassee ? 'cvn-depasse' : ''}><input type="date" defaultValue={isoJour(l.eta)} onBlur={(e) => e.target.value !== isoJour(l.eta) && maj(l, { eta: e.target.value || null })} title={depassee ? 'ETA dépassée : le lot est-il arrivé ?' : ''} /></td>
                          <td><input className="cvn-ref" defaultValue={l.ref_constructeur || ''} placeholder="à saisir" onBlur={(e) => e.target.value.trim() !== (l.ref_constructeur || '') && maj(l, { ref_constructeur: e.target.value })} /></td>
                          <td><input className="cvn-prix" type="number" min="0" step="100" defaultValue={l.prix_unitaire_ht ?? ''} onBlur={(e) => String(e.target.value) !== String(l.prix_unitaire_ht ?? '') && maj(l, { prix_unitaire_ht: e.target.value })} /></td>
                          <td className="cvn-fort">{eur(l.montant_ht)}</td>
                          <td><input type="date" defaultValue={isoJour(l.date_paiement_prevue)} className={l.paiement_manuel ? 'manuel' : ''} title={l.paiement_manuel ? 'Saisie à la main (videz pour revenir au calcul)' : 'Calculée : ETA + délai de paiement'} onBlur={(e) => e.target.value !== isoJour(l.date_paiement_prevue) && maj(l, { date_paiement_prevue: e.target.value || null })} /></td>
                          <td className="l"><label className="cvn-paye"><input type="checkbox" checked={l.paye} onChange={(e) => maj(l, { paye: e.target.checked })} />{l.paye && <small>{dateFr(l.date_paiement)}</small>}</label></td>
                          <td className="l">
                            <div className="action-buttons">
                              {l.quantite > 1 && <button type="button" className="btn-icon" title="Fractionner : une partie arrive à une autre date" onClick={() => setFrac({ lot: l, quantite: Math.floor(l.quantite / 2), eta: isoJour(l.eta) })}>✂️</button>}
                              {peutSupprimer && <button type="button" className="btn-icon delete" title="Supprimer" onClick={() => supprimerLot(l)}>🗑️</button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {lots.length === 0 && <tr><td colSpan={10} className="empty-state">Aucun lot : ajoutez les versions commandées.</td></tr>}
                  </tbody>
                </table>
              </div>

              {!ajout ? (
                <button type="button" className="btn-secondary cvn-ajout" onClick={() => setAjout({ version_id: versions[0]?.id || '', quantite: 1, statut: 'commande', eta: '' })}>+ Ajouter une version</button>
              ) : (
                <div className="form-grid cvn-ajout-form">
                  <div className="form-group full"><label>Version</label>
                    <select value={ajout.version_id} onChange={(e) => setAjout({ ...ajout, version_id: Number(e.target.value) })}>
                      {versions.map((v) => <option key={v.id} value={v.id}>{v.modele} · {v.libelle}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Quantité</label><input type="number" min="1" value={ajout.quantite} onChange={(e) => setAjout({ ...ajout, quantite: Number(e.target.value) })} /></div>
                  <div className="form-group"><label>Statut</label>
                    <select value={ajout.statut} onChange={(e) => setAjout({ ...ajout, statut: e.target.value })}>
                      {STATUTS.filter((s) => s.code !== 'livre').map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>ETA</label><input type="date" value={ajout.eta} onChange={(e) => setAjout({ ...ajout, eta: e.target.value })} /></div>
                  <div className="form-group cvn-form-actions">
                    <button type="button" className="btn-secondary" onClick={() => setAjout(null)}>Annuler</button>
                    <button type="button" className="btn-primary" disabled={!ajout.version_id || !(ajout.quantite > 0)} onClick={ajouter}>Ajouter</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          {peutSupprimer && data && <button type="button" className="btn-secondary cvn-danger cvn-footer-gauche" onClick={supprimer}>🗑️ Supprimer la commande</button>}
          <button type="button" className="btn-secondary" onClick={fermer}>Fermer</button>
        </div>
      </div>

      {frac && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✂️ Fractionner le lot</h2>
              <button type="button" className="modal-close" onClick={() => setFrac(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p className="cvn-regle">{frac.lot.quantite} × {frac.lot.version} : une partie arrive à une autre date.</p>
              <div className="form-grid">
                <div className="form-group"><label>Véhicules déplacés</label><input type="number" min="1" max={frac.lot.quantite - 1} value={frac.quantite} onChange={(e) => setFrac({ ...frac, quantite: e.target.value })} /></div>
                <div className="form-group"><label>ETA de cette partie</label><input type="date" value={frac.eta} onChange={(e) => setFrac({ ...frac, eta: e.target.value })} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setFrac(null)}>Annuler</button>
              <button type="button" className="btn-primary" disabled={!(Number(frac.quantite) > 0 && Number(frac.quantite) < frac.lot.quantite)} onClick={fractionner}>Fractionner</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CALENDRIER
// ═══════════════════════════════════════════════════════════════════════════
function Calendrier({ concessionId, marque, setHeaderAction }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);
  useEffect(() => { setData(null); api.calendrier(concessionId, marque).then((r) => setData(r.data)).catch((e) => alert(erreurDe(e))); }, [concessionId, marque]);
  if (!data) return <div className="empty-state">Chargement…</div>;
  // Frise : les MOIS en colonnes (lecture de gauche à droite), en lignes les
  // arrivées par modèle, leur total, puis les décaissements.
  const mois = [...new Set([...Object.keys(data.arrivees), ...Object.keys(data.paiements)])].sort();
  // Modèles dans l'ordre fixe des versions suivies (comme la couverture).
  const presents = new Set(Object.values(data.arrivees).flatMap((a) => Object.keys(a.par_modele)));
  const modeles = [...(data.modeles || []).filter((m) => presents.has(m)), ...[...presents].filter((m) => !(data.modeles || []).includes(m)).sort()];
  const ymNow = aujourdhui().slice(0, 7);
  const totalA = Object.values(data.arrivees).reduce((s, a) => s + a.total, 0);
  const totalP = Object.values(data.paiements).reduce((s, p) => s + p.montant, 0);
  const sansDate = data.sans_eta.vehicules > 0 || data.sans_date_paiement.lots > 0;
  const classeMois = (ym) => (ym < ymNow ? 'cvn-retard' : ym === ymNow ? 'cvn-courant' : '');
  const parModele = (m) => mois.reduce((s, ym) => s + (data.arrivees[ym]?.par_modele[m] || 0), 0);
  return (
    <>
      <div className="cvn-kpis">
        <div className="cvn-kpi"><span className="l">À arriver</span><span className="v">{nf.format(totalA + data.sans_eta.vehicules)}</span><span className="d">véhicules{data.sans_eta.vehicules ? ` · dont ${data.sans_eta.vehicules} sans ETA` : ''}</span></div>
        <div className="cvn-kpi"><span className="l">À décaisser</span><span className="v">{keur(totalP + data.sans_date_paiement.montant)}</span><span className="d">HT{data.sans_date_paiement.lots ? ` · dont ${keur(data.sans_date_paiement.montant)} sans date` : ''}</span></div>
      </div>
      <p className="cvn-regle">Lots non livrés (arrivées) et non payés (décaissements), mois par mois. Un mois passé en rouge = en retard : les lots sont-ils arrivés, ou payés ?</p>
      {mois.length === 0 && !sansDate ? <div className="empty-state">Rien à arriver ni à payer.</div> : (
        <div className="table-container cvn-scroll">
          <table className="cvn-table cvn-cal">
            <thead>
              <tr>
                <th className="l" />
                {mois.map((ym) => <th key={ym} className={classeMois(ym)} title={ym < ymNow ? 'Mois passé : en retard' : ''}>{moisLib(ym)}{ym < ymNow ? ' ⚠' : ''}</th>)}
                {sansDate && <th title="Lots sans ETA ou sans date de paiement">Sans date</th>}
                <th className="cvn-cal-total">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="cvn-groupe"><td className="l" colSpan={mois.length + (sansDate ? 3 : 2)}>Arrivées (véhicules)</td></tr>
              {modeles.map((m) => (
                <tr key={m}>
                  <td className="l">{m}</td>
                  {mois.map((ym) => <td key={ym} className={classeMois(ym)}>{data.arrivees[ym]?.par_modele[m] || ''}</td>)}
                  {sansDate && <td />}
                  <td className="cvn-cal-total">{parModele(m) || ''}</td>
                </tr>
              ))}
              <tr className="cvn-cal-somme">
                <td className="l">Total arrivées</td>
                {mois.map((ym) => <td key={ym} className={classeMois(ym)}>{data.arrivees[ym]?.total || ''}</td>)}
                {sansDate && <td>{data.sans_eta.vehicules || ''}</td>}
                <td className="cvn-cal-total">{nf.format(totalA + data.sans_eta.vehicules)}</td>
              </tr>
              <tr className="cvn-groupe"><td className="l" colSpan={mois.length + (sansDate ? 3 : 2)}>Décaissements</td></tr>
              <tr className="cvn-cal-somme">
                <td className="l">Montant HT</td>
                {mois.map((ym) => {
                  const p = data.paiements[ym];
                  return <td key={ym} className={classeMois(ym)} title={p?.sans_prix ? `${p.sans_prix} lot(s) sans prix : montant incomplet` : ''}>{p ? keur(p.montant) : ''}{p?.sans_prix > 0 ? ' *' : ''}</td>;
                })}
                {sansDate && <td>{data.sans_date_paiement.lots ? keur(data.sans_date_paiement.montant) : ''}</td>}
                <td className="cvn-cal-total">{keur(totalP + data.sans_date_paiement.montant)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {Object.values(data.paiements).some((p) => p.sans_prix > 0) && <p className="cvn-regle">* montant incomplet : des lots de ce mois n'ont pas de prix unitaire.</p>}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PARAMÈTRES
// ═══════════════════════════════════════════════════════════════════════════
function Parametres({ concessionId, marque, recherche, setHeaderAction }) {
  const [couv, setCouv] = useState(null);
  const [section, setSection] = useState(null);
  const [corr, setCorr] = useState(null);
  const [p, setP] = useState(null);
  const [nouvelle, setNouvelle] = useState(null);

  const charger = useCallback(() => {
    api.couverture(concessionId, marque).then((r) => { setCouv(r.data); setP(r.data.parametres); }).catch((e) => alert(erreurDe(e)));
    api.correspondances(concessionId, marque).then((r) => setCorr(r.data.correspondances)).catch(() => setCorr([]));
  }, [concessionId, marque]);
  useEffect(() => { charger(); }, [charger]);

  const nbProposees = (corr || []).filter((k) => k.propose).length;
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({
      primary: { label: '+ Version', onClick: () => setNouvelle({ modele: '', libelle: '' }) },
      secondary: nbProposees ? [{ label: `✓ Confirmer les ${nbProposees} propositions`, onClick: async () => { try { await api.confirmerPropositions(concessionId, marque); charger(); } catch (e) { alert(erreurDe(e)); } } }] : [],
    });
  }, [setHeaderAction, nbProposees, concessionId, marque, charger]);

  if (!couv || !p) return <div className="empty-state">Chargement…</div>;
  const enregistrer = async () => {
    try { await api.enregistrerParametres(concessionId, marque, p); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const majVersion = async (v, champs) => { try { await api.modifierVersion(v.id, champs); charger(); } catch (e) { alert(erreurDe(e)); } };
  const classer = async (k, valeur) => {
    try { await api.classer(k.id, valeur === 'ignore' ? { ignore: true } : { version_id: valeur ? Number(valeur) : null }); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const ajouterVersion = async () => {
    try { await api.ajouterVersion(concessionId, marque, nouvelle); setNouvelle(null); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const activite = new Map((couv.a_classer || []).map((a) => [a.correspondance_id, a]));
  const libVersion = new Map(couv.versions.map((v) => [v.id, v.libelle]));
  const aClasser = (k) => !k.version_id && !k.ignore;
  const nb = { a_classer: (corr || []).filter(aClasser).length, a_verifier: (corr || []).filter((k) => k.propose).length };
  // Sous-onglet par défaut : ce qui demande une action d'abord.
  const sec = section || (nb.a_classer ? 'a_classer' : nb.a_verifier ? 'a_verifier' : 'delais');
  const SECTIONS = [['delais', '⏱️ Délais'], ['a_classer', `À classer (${nb.a_classer})`], ['a_verifier', `Propositions à vérifier (${nb.a_verifier})`],
    ['libelles', `Tous les libellés (${(corr || []).length})`], ['versions', `Versions suivies (${couv.versions.length})`]];
  const garde = { a_classer: aClasser, a_verifier: (k) => k.propose, libelles: () => true };
  const listees = (corr || []).filter((k) => (garde[sec] || (() => false))(k) && contient(recherche, k.serie, k.modele_dms, libVersion.get(k.version_id)));
  const versionsListees = couv.versions.filter((v) => contient(recherche, v.modele, v.libelle));

  return (
    <>
      <div className="header-segmented cvn-sections">
        {SECTIONS.map(([k, l]) => <button key={k} type="button" className={`header-segmented-btn${sec === k ? ' active' : ''}`} onClick={() => setSection(k)}>{l}</button>)}
      </div>

      {sec === 'delais' && <div className="cvn-bloc">
        <div className="form-grid cvn-delais">
          <div className="form-group"><label>Délai de commande (mois)</label><input type="number" step="0.5" min="0" value={p.delai_commande_mois} onChange={(e) => setP({ ...p, delai_commande_mois: e.target.value })} /><small>de la commande à l'arrivée chez nous</small></div>
          <div className="form-group"><label>Couverture voulue (mois)</label><input type="number" step="0.5" min="0" value={p.couverture_cible_mois} onChange={(e) => setP({ ...p, couverture_cible_mois: e.target.value })} /><small>stock de sécurité, au-delà du délai</small></div>
          <div className="form-group"><label>Délai de paiement (jours)</label><input type="number" min="0" value={p.delai_paiement_jours} onChange={(e) => setP({ ...p, delai_paiement_jours: e.target.value })} /><small>ETA + ce délai = paiement prévu</small></div>
          <div className="form-group cvn-form-actions"><button type="button" className="btn-primary" onClick={enregistrer}>Enregistrer</button></div>
        </div>
        <p className="cvn-regle">Une version peut avoir son propre délai de commande (colonne « Délai propre » des versions suivies) : il remplace celui de la marque.</p>
      </div>}

      {['a_classer', 'a_verifier', 'libelles'].includes(sec) && <>
      <p className="cvn-regle">{sec === 'a_classer' ? 'Libellés du DMS rattachés à aucune version : leurs ventes et leur stock sont hors couverture. Choisissez la version, ou « Ignorer ». ' : sec === 'a_verifier' ? 'Rattachements proposés automatiquement (ils comptent déjà dans la couverture). Corrigez ceux qui sont faux, puis « Confirmer les propositions » en haut à droite. ' : ''}Le DMS nomme les versions à sa façon (« GL 1,2 5MT 2WD », « 2024 SWIFF 1.2 5MT GL »…). Chaque libellé est rattaché UNE fois à une version de suivi ; les nouveaux apparaissent ici. Les propositions automatiques comptent déjà dans la couverture : vérifiez-les.</p>
      <div className="table-container cvn-scroll">
        <table className="cvn-table">
          <thead><tr><th className="l">Série</th><th className="l">Libellé DMS</th><th>Ventes 24 m</th><th>Stock</th><th className="l">Version de suivi</th></tr></thead>
          <tbody>
            {listees.map((k) => {
              const a = activite.get(k.id);
              return (
                <tr key={k.id} className={k.propose ? 'cvn-propose' : (!k.version_id && !k.ignore ? 'cvn-aclasser' : '')}>
                  <td className="l">{k.serie || '—'}</td>
                  <td className="l font-mono">{k.modele_dms || '—'}</td>
                  <td>{a ? a.ventes_24m : ''}</td>
                  <td>{a ? a.stock : ''}</td>
                  <td className="l">
                    <select value={k.ignore ? 'ignore' : (k.version_id || '')} onChange={(e) => classer(k, e.target.value)}>
                      <option value="">— à classer —</option>
                      {couv.versions.map((v) => <option key={v.id} value={v.id}>{v.modele} · {v.libelle}</option>)}
                      <option value="ignore">Ignorer (hors suivi)</option>
                    </select>
                    {k.propose && <span className="badge-pill badge-info cvn-badge-ligne">proposée</span>}
                  </td>
                </tr>
              );
            })}
            {listees.length === 0 && <tr><td colSpan={5} className="empty-state">{sec === 'a_classer' ? 'Tous les libellés sont rattachés.' : sec === 'a_verifier' ? 'Aucune proposition en attente.' : 'Aucun libellé.'}</td></tr>}
          </tbody>
        </table>
      </div>

      </>}

      {sec === 'versions' && <div className="table-container cvn-scroll">
        <table className="cvn-table">
          <thead><tr><th className="l">Modèle</th><th className="l">Libellé</th><th title="Remplace le délai de commande de la marque">Délai propre (mois)</th><th>Prix d'achat HT</th><th className="l">Suivie</th></tr></thead>
          <tbody>
            {versionsListees.map((v) => (
              <tr key={v.id} className={!v.actif ? 'cvn-livre' : ''}>
                <td className="l">{v.modele}</td>
                <td className="l"><input className="cvn-libelle" defaultValue={v.libelle} onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== v.libelle && majVersion(v, { libelle: e.target.value })} /></td>
                <td><input className="cvn-num" type="number" step="0.5" min="0" defaultValue={v.delai_propre ? v.delai_commande_mois : ''} placeholder={String(p.delai_commande_mois)} onBlur={(e) => e.target.value !== (v.delai_propre ? String(v.delai_commande_mois) : '') && majVersion(v, { delai_commande_mois: e.target.value })} /></td>
                <td><input className="cvn-prix" type="number" min="0" step="100" defaultValue={v.prix_achat_ht ?? ''} onBlur={(e) => String(e.target.value) !== String(v.prix_achat_ht ?? '') && majVersion(v, { prix_achat_ht: e.target.value })} /></td>
                <td className="l"><input type="checkbox" checked={v.actif} onChange={(e) => majVersion(v, { actif: e.target.checked })} /></td>
              </tr>
            ))}
            {versionsListees.length === 0 && <tr><td colSpan={5} className="empty-state">Aucune version.</td></tr>}
          </tbody>
        </table>
      </div>}

      {nouvelle && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle version {marque}</h2>
              <button type="button" className="modal-close" onClick={() => setNouvelle(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Modèle *</label><input value={nouvelle.modele} autoFocus placeholder="SWIFT" onChange={(e) => setNouvelle({ ...nouvelle, modele: e.target.value })} /></div>
                <div className="form-group"><label>Libellé *</label><input value={nouvelle.libelle} placeholder="Swift 1,2 GL Hybride" onChange={(e) => setNouvelle({ ...nouvelle, libelle: e.target.value })} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setNouvelle(null)}>Annuler</button>
              <button type="button" className="btn-primary" disabled={!nouvelle.modele.trim() || !nouvelle.libelle.trim()} onClick={ajouterVersion}>Ajouter</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
